/** The splices, weakest first: a stronger one reaches everything a weaker one does. */
export type SpliceGrade = 'gene' | 'prime';
export const SPLICE_GRADES: readonly SpliceGrade[] = ['gene', 'prime'];

/**
 * The roster. Numbers follow the plan and are a starting point for balance.
 *
 * What the game knows about a dinosaur is written on it, not read from where
 * it stands in this list (docs/era-residents-plan.md, §4): its tier number,
 * the splice that evolves it, its art's size. The list is in price order,
 * and era residents will join it among the fifteen.
 */
export interface DinoDef {
  id: string;
  name: string;
  /**
   * Its tier, 1-15, as the player counts them: what the boss fight's power,
   * analytics and the yard's depth rows go by. A resident shares the tier of
   * the dinosaur it follows in the roster.
   */
  tier: number;
  /** The weakest splice that evolves it. */
  splice: SpliceGrade;
  /** Native size of its full art, 64 px unless it is one of the big ones. */
  artSize?: number;
  /** A resident's era: it can only be hatched in that era. None for the fifteen. */
  era?: string;
  baseCost: number;
  /** Eggs per second laid by one of these. */
  baseEps: number;
  flavor: string;
  /** Palette color for the placeholder icon and nest sprite until M6 art lands. */
  color: string;
  /** Flyers live in the sky part of the yard instead of on the ground. */
  flies?: boolean;
  /**
   * Swimmers live in the stream along the bottom of the yard, drawn cut off
   * at the waterline (docs/water-plan.md, D3). The evolved form inherits it.
   */
  swims?: boolean;
  /** Roaming speed in the yard relative to the herd's usual pace (1). */
  pace?: number;
  /**
   * What a Gene Splice turns this tier into. The evolved form keeps the
   * tier's colour, flight and pace, its level, its name and its doublers;
   * only the species name, the sprites and the rate change.
   */
  evolved: EvolvedForm;
}

export interface EvolvedForm {
  /** Sprite id and save id for the evolution; the tier's own id never changes. */
  id: string;
  name: string;
  flavor: string;
}

/** An evolution multiplies its tier's base rate by this. */
/**
 * "a" or "an" for a species name, by how it is said: every vowel-initial
 * name here starts with a vowel sound except Utahraptor ("you-tah").
 */
export function aOrAn(name: string): 'a' | 'an' {
  return /^[aeio]/i.test(name) ? 'an' : 'a';
}

export const EVOLVED_EPS_MULTIPLIER = 3;

/** A tier must be at least this level before a splice can be used on it. */
export const EVOLVE_MIN_LEVEL = 25;

/** The fifteen tiers, in order. */
const TIERS: readonly DinoDef[] = [
  {
    id: 'compsognathus',
    name: 'Compsognathus',
    tier: 1,
    splice: 'gene',
    baseCost: 15,
    baseEps: 0.1,
    flavor: 'Tiny, skittish, lays constantly.',
    color: '#63c74d',
    evolved: {
      id: 'sinosauropteryx',
      name: 'Sinosauropteryx',
      flavor: 'The same skittish scamper, now in ginger-banded feathers.',
    },
  },
  {
    id: 'gallimimus',
    name: 'Gallimimus',
    tier: 2,
    splice: 'gene',
    baseCost: 100,
    baseEps: 1,
    flavor: 'Fast runner, gathers eggs from afar.',
    color: '#feae34',
    pace: 4,
    evolved: {
      id: 'ornithomimus',
      name: 'Ornithomimus',
      flavor: 'Fully feathered; the arm plumes catch the wind as it runs.',
    },
  },
  {
    id: 'oviraptor',
    name: 'Oviraptor',
    tier: 3,
    splice: 'gene',
    baseCost: 1_100,
    baseEps: 8,
    flavor: 'Infamous egg thief, now working for you.',
    color: '#0099db',
    evolved: {
      id: 'gigantoraptor',
      name: 'Gigantoraptor',
      flavor: 'The egg thief at eight metres. Nobody argues with it now.',
    },
  },
  {
    id: 'triceratops',
    name: 'Triceratops',
    tier: 4,
    splice: 'gene',
    baseCost: 12_000,
    baseEps: 47,
    flavor: 'Guards the nest.',
    color: '#e43b44',
    evolved: {
      id: 'torosaurus',
      name: 'Torosaurus',
      flavor: 'The frill grown to its full span, with two windows in it.',
    },
  },
  {
    id: 'stegosaurus',
    name: 'Stegosaurus',
    tier: 5,
    splice: 'gene',
    baseCost: 130_000,
    baseEps: 260,
    flavor: 'Slow but prolific.',
    color: '#b55088',
    evolved: {
      id: 'kentrosaurus',
      name: 'Kentrosaurus',
      flavor: 'Plates give way to paired spikes from shoulder to tail.',
    },
  },
  {
    id: 'ankylosaurus',
    name: 'Ankylosaurus',
    tier: 6,
    splice: 'gene',
    baseCost: 1_400_000,
    baseEps: 1_400,
    flavor: 'Armored incubator.',
    color: '#f77622',
    evolved: {
      id: 'sauropelta',
      name: 'Sauropelta',
      flavor: 'Shoulder spikes over a studded shell; the club stays.',
    },
  },
  {
    id: 'parasaurolophus',
    name: 'Parasaurolophus',
    tier: 7,
    splice: 'gene',
    baseCost: 20_000_000,
    baseEps: 7_800,
    flavor: 'Its call summons the herd to lay.',
    color: '#2ce8f5',
    evolved: {
      id: 'lambeosaurus',
      name: 'Lambeosaurus',
      flavor: 'A hatchet crest that carries its call twice as far.',
    },
  },
  {
    id: 'pteranodon',
    name: 'Pteranodon',
    tier: 8,
    splice: 'gene',
    baseCost: 330_000_000,
    baseEps: 44_000,
    flavor: 'Airlifts eggs from cliffside nests.',
    color: '#3e8948',
    flies: true,
    evolved: {
      id: 'tropeognathus',
      name: 'Tropeognathus',
      flavor: 'A keel-jawed ocean crosser with a broader wing.',
    },
  },
  {
    id: 'velociraptor',
    name: 'Velociraptor pack',
    tier: 9,
    splice: 'prime',
    baseCost: 5.1e9,
    baseEps: 260_000,
    flavor: 'Raids rival nests.',
    color: '#fee761',
    evolved: {
      id: 'utahraptor',
      name: 'Utahraptor pack',
      flavor: 'The same pack, each raptor five times the size.',
    },
  },
  {
    id: 'brachiosaurus',
    name: 'Brachiosaurus',
    tier: 10,
    splice: 'prime',
    baseCost: 75e9,
    baseEps: 1.6e6,
    flavor: 'Reaches the highest canopy nests.',
    color: '#f6757a',
    evolved: {
      id: 'sauroposeidon',
      name: 'Sauroposeidon',
      flavor: 'The tallest thing that ever walked, and still reaching.',
    },
  },
  {
    id: 'spinosaurus',
    name: 'Spinosaurus',
    tier: 11,
    splice: 'prime',
    baseCost: 1e12,
    baseEps: 10e6,
    flavor: 'Dredges eggs from the river delta.',
    color: '#124e89',
    evolved: {
      id: 'ichthyovenator',
      name: 'Ichthyovenator',
      flavor: 'The sail splits in two and the river-hunter takes to the water.',
    },
  },
  {
    id: 'tyrannosaurus',
    name: 'Tyrannosaurus',
    tier: 12,
    splice: 'prime',
    baseCost: 14e12,
    baseEps: 65e6,
    flavor: 'Apex layer; nothing dares steal from it.',
    color: '#d77643',
    evolved: {
      id: 'tyrannosaurus-alpha',
      name: 'Tyrannosaurus Rex Alpha',
      flavor: 'Not another species. The same one, scarred and crowned.',
    },
  },
  {
    id: 'mosasaurus',
    name: 'Mosasaurus',
    tier: 13,
    splice: 'prime',
    artSize: 96,
    baseCost: 170e12,
    baseEps: 430e6,
    flavor: 'Harvests the deep-sea nesting grounds.',
    color: '#68386c',
    swims: true,
    evolved: {
      id: 'tylosaurus',
      name: 'Tylosaurus',
      flavor: 'Longer, ram-snouted, and unopposed in the deep.',
    },
  },
  {
    id: 'quetzalcoatlus',
    name: 'Quetzalcoatlus',
    tier: 14,
    splice: 'prime',
    artSize: 96,
    baseCost: 2.1e15,
    baseEps: 2.9e9,
    flavor: 'Spans continents collecting eggs.',
    color: '#265c42',
    flies: true,
    evolved: {
      id: 'hatzegopteryx',
      name: 'Hatzegopteryx',
      flavor: 'A giraffe that flies and hunts what it finds below.',
    },
  },
  {
    id: 'baryonyx',
    name: 'Baryonyx',
    tier: 15,
    splice: 'prime',
    // Long rather than tall: 96 wide to fit its length, the animal 48 rows high
    // at the bottom of the square, so it stands at the cast's own pixel scale.
    artSize: 96,
    baseCost: 26e15,
    baseEps: 21e9,
    flavor: 'Named for one heavy claw, which fishes eggs out of the river as easily as trout.',
    color: '#be4a2f',
    evolved: {
      id: 'suchomimus',
      name: 'Suchomimus',
      flavor: 'A crocodile face, a ridge along the back, and both heavy claws.',
    },
  },
];

/**
 * Era residents (docs/era-residents-plan.md): one dinosaur of its own for
 * each era but the plain one, a normal dinosaur in every way that can only be
 * hatched in its era. Each sits in the roster where its price puts it, and
 * shares the tier number of the dinosaur it follows. Prices and rates are the
 * geometric middle of their neighbours', a first guess for the simulator.
 */
const RESIDENTS: readonly DinoDef[] = [
  {
    id: 'shuvuuia',
    name: 'Shuvuuia',
    tier: 2,
    splice: 'gene',
    era: 'era:night',
    baseCost: 330,
    baseEps: 2.8,
    flavor: 'Owl-eyed and sharp-eared, it hunts the yard after dark.',
    color: '#8b9bb4',
    evolved: {
      id: 'mononykus',
      name: 'Mononykus',
      flavor: 'One great claw on each arm, for digging out whatever the dark hid.',
    },
  },
  {
    id: 'microraptor',
    name: 'Microraptor',
    tier: 3,
    splice: 'gene',
    era: 'era:cretaceous',
    baseCost: 3_600,
    baseEps: 19,
    flavor: 'Four feathered wings and a glossy black coat. Glides more than it flies.',
    color: '#3a4466',
    flies: true,
    evolved: {
      id: 'anchiornis',
      name: 'Anchiornis',
      flavor:
        'Four wings, a red crest and spangled feathers: one of the first dinosaurs worn in its true colours.',
    },
  },
  {
    id: 'pyroraptor',
    name: 'Pyroraptor',
    tier: 4,
    splice: 'gene',
    era: 'era:volcanic',
    baseCost: 40_000,
    baseEps: 110,
    flavor: 'Named for the fire it was found after, and unbothered by the heat.',
    color: '#e43b44',
    evolved: {
      id: 'balaur',
      name: 'Balaur',
      flavor: 'A stocky little dragon with two sickle claws on each foot.',
    },
  },
  {
    id: 'allosaurus',
    name: 'Allosaurus',
    tier: 5,
    splice: 'gene',
    era: 'era:jurassic',
    baseCost: 430_000,
    baseEps: 600,
    flavor: 'The Jurassic’s own great hunter. Its nest is not up for discussion.',
    color: '#be4a2f',
    evolved: {
      id: 'saurophaganax',
      name: 'Saurophaganax',
      flavor: 'Lord of the lizard-eaters, and well aware of it.',
    },
  },
  {
    id: 'moa',
    name: 'Moa',
    tier: 8,
    splice: 'gene',
    era: 'era:ice-age',
    baseCost: 1.3e9,
    baseEps: 107_000,
    flavor: 'A bird, which makes it a dinosaur. Tall, flightless and unfazed by snow.',
    color: '#c0cbdc',
    evolved: {
      id: 'elephant-bird',
      name: 'Elephant bird',
      flavor: 'The heaviest bird that ever lived. One of its eggs feeds a village.',
    },
  },
  // The Giant era's resident, and the one resident above the fifteen: until
  // 2026-09-29 it was tier 15 (Baryonyx took the slot) and the Mussaurus was
  // Giant's resident, retired as a poor fit. Half a tier's step above tier 15.
  {
    id: 'argentinosaurus',
    name: 'Argentinosaurus',
    tier: 15,
    splice: 'prime',
    era: 'era:giant',
    artSize: 96,
    baseCost: 92e15,
    baseEps: 57e9,
    flavor: 'So vast its nest is a valley.',
    color: '#be4a2f',
    evolved: {
      id: 'patagotitan',
      name: 'Patagotitan',
      flavor: 'The largest titanosaur. Its nest is a valley of its own.',
    },
  },
];

/**
 * Residents are being built (docs/era-residents-plan.md, R1): in the roster
 * in development, so the dev server, the tests and the simulator have them,
 * and kept off the live site until their own art lands (R2).
 */
export const RESIDENTS_LIVE: boolean = import.meta.env.DEV;

/** Every dinosaur, in price order: the fifteen tiers and the era residents. */
export const DINOS: readonly DinoDef[] = RESIDENTS_LIVE
  ? [...TIERS, ...RESIDENTS].sort((a, b) => a.baseCost - b.baseCost)
  : TIERS;

/** The era residents' ids, for the achievement that asks for all of them. */
export const RESIDENT_IDS: readonly string[] = RESIDENTS_LIVE ? RESIDENTS.map((d) => d.id) : [];

export const DINOS_BY_ID: ReadonlyMap<string, DinoDef> = new Map(DINOS.map((d) => [d.id, d]));

/**
 * The dinosaurs an era offers: every one without an era of its own, and the
 * era's resident if it has one. The fifteen in every era today.
 */
export function availableDinos(eraId: string | null): DinoDef[] {
  return DINOS.filter((d) => d.era === undefined || d.era === eraId);
}

/** Whether a splice of this grade can evolve this dinosaur. */
export function spliceReaches(grade: SpliceGrade, def: DinoDef): boolean {
  return SPLICE_GRADES.indexOf(grade) >= SPLICE_GRADES.indexOf(def.splice);
}

/** Native size of a dinosaur's art: its full sprite, or the yard's half-size one. */
export function artSizeOf(def: DinoDef, kind: 'full' | 'yard'): number {
  const full = def.artSize ?? 64;
  return kind === 'yard' ? full / 2 : full;
}
